/**
 * FeedbackHeaderBadge — Admin-only header icon with live counts from the feedback pipeline.
 * Shows total open issues, new count, and agent activity indicator.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFeedbackStats } from './feedbackApi';
import usePolling from '../../utils/usePolling';
import './FeedbackHeaderBadge.css';

// 5 min i synlig flik, paus i dold flik (A11, 2026-10-07).
const POLL_INTERVAL = 5 * 60 * 1000;

export default function FeedbackHeaderBadge() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const ref = useRef(null);
  const leaveTimer = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const data = await getFeedbackStats();
      setStats(data);
      return true;
    } catch {
      // Silently fail — badge just won't update; usePolling glesar ut
      return false;
    }
  }, []);

  usePolling(fetchStats, { intervalMs: POLL_INTERVAL });

  // Click outside to close
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(leaveTimer.current), []);

  const handleEnter = () => {
    clearTimeout(leaveTimer.current);
    setDropdownOpen(true);
  };
  const handleLeave = () => {
    leaveTimer.current = setTimeout(() => setDropdownOpen(false), 200);
  };

  const incoming = stats?.incoming || 0;
  const analyzing = stats?.analyzing || 0;
  const proposed = stats?.ready_to_approve || 0;
  const totalOpen = incoming + analyzing + proposed;
  const agentActive = analyzing > 0;

  return (
    <div className="fb-hdr-badge-wrap" ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        className="hb-icon-btn fb-hdr-btn"
        onClick={() => navigate('/feedback-board')}
        title="Feedback Pipeline — Öppna dashboard"
      >
        {/* Bug icon */}
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 2l1.88 1.88M16 2l-1.88 1.88M9 7.13v-1a3 3 0 0 1 6 0v1" />
          <path d="M12 20c-3.3 0-6-2.7-6-6v-3a6 6 0 0 1 12 0v3c0 3.3-2.7 6-6 6z" />
          <path d="M6 13H2M22 13h-4M6 17H3M21 17h-3M6 9H4M20 9h-2" />
        </svg>
        {totalOpen > 0 && (
          <span className="hb-badge-count fb-hdr-count">{totalOpen > 99 ? '99+' : totalOpen}</span>
        )}
        {agentActive && <span className="fb-hdr-agent-dot" title="KanAI analyserar" />}
      </button>

      {dropdownOpen && (
        <div className="fb-hdr-dropdown">
          <div className="fb-hdr-dropdown-title">Feedback Pipeline</div>
          <div className="fb-hdr-dropdown-rows">
            <div className="fb-hdr-row">
              <span className="fb-hdr-row-dot" style={{ background: '#D97706' }} />
              <span className="fb-hdr-row-label">Nya</span>
              <span className="fb-hdr-row-count">{incoming}</span>
            </div>
            <div className="fb-hdr-row">
              <span className="fb-hdr-row-dot" style={{ background: '#7C3AED' }} />
              <span className="fb-hdr-row-label">KanAI analyserar</span>
              <span className="fb-hdr-row-count">{analyzing}</span>
              {agentActive && <span className="fb-hdr-row-pulse" />}
            </div>
            <div className="fb-hdr-row">
              <span className="fb-hdr-row-dot" style={{ background: '#059669' }} />
              <span className="fb-hdr-row-label">Godkännande</span>
              <span className="fb-hdr-row-count">{proposed}</span>
            </div>
          </div>
          <button
            className="fb-hdr-dropdown-link"
            onClick={() => { navigate('/feedback-board'); setDropdownOpen(false); }}
          >
            Öppna dashboard
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}
